/**
 * feAI – Frontend
 * src/components/permissions/PageGuard.tsx
 *
 * Guardia di accesso a livello di pagina.
 * Controlla che l'utente autenticato abbia il permesso can_use sulla pagina
 * prima di renderizzarne il contenuto. In caso contrario mostra un avviso.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../lib/apiClient';
import { colors, typography } from '../../lib/designTokens';

interface PageGuardProps {
  pageId: string;
  children: React.ReactNode;
}

interface AccessCheck {
  page_id:  string;
  can_view: boolean;
  can_use:  boolean;
  source:   string;
}

export const PageGuard: React.FC<PageGuardProps> = ({ pageId, children }) => {
  const [check, setCheck] = useState<AccessCheck | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const { data } = await apiClient.get('/api/v1/core/admin/permissions/check', {
          params: { page: pageId },
        });
        if (!cancelled) setCheck(data.data);
      } catch (err) {
        console.warn('Errore verifica permesso pagina:', err);
        if (!cancelled) {
          // Fallback di sicurezza: se il modulo permessi non è disponibile,
          // le pagine admin vengono negate, le altre consentite (retrocompatibilità)
          const isAdminPage = pageId.startsWith('admin/');
          setCheck({ page_id: pageId, can_view: !isAdminPage, can_use: !isAdminPage, source: 'default' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [pageId]);

  if (loading) {
    return (
      <div style={{
        padding: 60,
        textAlign: 'center' as const,
        fontFamily: typography.fontFamily,
        color: '#9ca3af',
        fontSize: '0.9rem',
      }}>
        Verifica permessi...
      </div>
    );
  }

  if (!check?.can_use) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: 40,
        textAlign: 'center' as const,
        fontFamily: typography.fontFamily,
      }}>
        <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🔒</div>
        <div style={{ fontSize: '1.1rem', fontWeight: 700, color: colors.greenDark, marginBottom: 6 }}>
          Accesso non consentito
        </div>
        <div style={{ fontSize: '0.85rem', color: '#6b7280', maxWidth: 360, marginBottom: 20 }}>
          Non hai il permesso per accedere a questa pagina. Contatta un amministratore se ritieni che si tratti di un errore.
        </div>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '8px 20px',
            fontSize: '0.85rem',
            fontWeight: 600,
            border: 'none',
            borderRadius: 8,
            background: `linear-gradient(135deg, ${colors.greenDark}, ${colors.greenLight})`,
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          Vai alla prima pagina disponibile
        </button>
      </div>
    );
  }

  return <>{children}</>;
};

export default PageGuard;